import React, { useEffect, useState } from "react";
import { useStores } from "../../../use-stores";
import { observer } from "mobx-react";
import { Box, Button, useTheme, Grid2 } from "@mui/material";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import { DateRangePickerNew } from "../../../../../views/components/common/textFields/DatePickerRangeNew";
import { Dayjs } from "dayjs";
import { useSearchParams } from "react-router-dom";

import AppDropDown from "../../../../../views/components/common/filter/AppDropDown";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import { useNotification } from "../../../../../utils/NotificationsContext";
import LoaderOverlay from "../../../../../views/components/common/pages/AppLoader";
import { Admin } from "../../../../../models/TaskTracker/Admin";
import AppSearchDropDown2 from "../../../../../views/components/common/filter/AppSearchDropdown2";
import PermissionBlockLabel from "../../../../../views/components/common/pop-up/PermissionBlockLabel ";
import { EmployeeRoles } from "../../../../../models/Employees/EmployeeRoles";
import { SortType } from "../../../../../models/SortType";
import { FilterValue } from "../../../../../models/Filters/FilterValue";
import { Product } from "../../../../../models/Products/Product";
import { Customer } from "../../../../../models/Customer/Customer";
import OpsSyncAnalyticsTable from "../../components/tables/OpsSyncAnalyticsTable";
import {
  OpsSyncAnalyticsFilters,
  OpsSyncAnalyticsSelectedFilters,
  parseQueryParamsForOpsSyncAnalyticsFilters,
} from "../../../../../models/Dashboard/OpsSyncAnalyticsFilters";
import CustomTextField from "../../../../../views/components/common/textFields/TextField";

const OPS_SYNC_ANALYTICS_FILTERS_KEY = "OPS_SYNC_ANALYTICS_FILTERS_KEY";

const archivedOptionsList = [
  { id: "1", value: "Active" },
  { id: "2", value: "Archived" },
];

const OpsSyncAnalyticsDashboard: React.FC = () => {
  const theme = useTheme();

  const {
    opsSyncAnalyticsDashboardStore,
    projectStore,
    orderStore,
    TaskTrackerStore,
    userStore,
    productStore,
    customerStore,
  } = useStores();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedFiltersData, setSelectedFiltersData] = useState<FilterValue[]>(
    [],
  );
  const [loading, setLoading] = useState(false);
  const { showError } = useNotification();
  let apiTimer: any;

  const initialFilters: OpsSyncAnalyticsFilters =
    // parseQueryParamsForOpsSyncAnalyticsFilters(
    //   localStorage.getItem(OPS_SYNC_ANALYTICS_FILTERS_KEY)
    //     ? JSON.parse(localStorage.getItem(OPS_SYNC_ANALYTICS_FILTERS_KEY)!)
    //     : null,
    //   searchParams
    // );
    parseQueryParamsForOpsSyncAnalyticsFilters(null, searchParams);

  const [filters, setFilters] =
    useState<OpsSyncAnalyticsFilters>(initialFilters);
  const [selectedFilters, setSelectedFilters] =
    useState<OpsSyncAnalyticsSelectedFilters>({
      ...initialFilters,
      start_date: initialFilters.start_date.format("YYYY-MM-DD"),
      end_date: initialFilters.end_date.format("YYYY-MM-DD"),
    });

  useEffect(() => {
    updateSelectedFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    handleApply();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const newSearchParams = new URLSearchParams();
    Object.entries(selectedFilters).forEach(([key, value]) => {
      if (value) {
        if (Array.isArray(value)) {
          if (value.length > 0) {
            newSearchParams.set(key, value.map((item) => item.id).join(","));
          }
        } else {
          newSearchParams.set(key, value);
        }
      }
    });

    setSearchParams(newSearchParams);
  }, [selectedFilters]);

  const updateSelectedFilters = () => {
    localStorage.setItem(
      OPS_SYNC_ANALYTICS_FILTERS_KEY,
      JSON.stringify(filters),
    );

    const start_date = filters.start_date.format("YYYY-MM-DD");
    const end_date = filters.end_date.format("YYYY-MM-DD");

    setSelectedFilters((_) => ({
      ...filters,
      start_date: start_date,
      end_date: end_date,
    }));

    const newFilters: React.SetStateAction<FilterValue[]> = [];

    if (filters.pm_admin_id) {
      filters.pm_admin_id.forEach((pm_admin_id) =>
        newFilters.push({ id: "pm_admin_id", value: pm_admin_id.value }),
      );
    }

    if (filters.sales_admin_id) {
      filters.sales_admin_id.forEach((sales_admin_id) =>
        newFilters.push({ id: "sales_admin_id", value: sales_admin_id.value }),
      );
    }

    if (filters.order_id) {
      filters.order_id.forEach((order_id) =>
        newFilters.push({ id: "order_id", value: order_id.value }),
      );
    }
    if (filters.order_type) {
      filters.order_type.forEach((order_type) =>
        newFilters.push({ id: "order_type", value: order_type.value }),
      );
    }
    if (filters.product_ids) {
      filters.product_ids.forEach((product_ids) =>
        newFilters.push({ id: "product_ids", value: product_ids.value }),
      );
    }
    if (filters.customer_ids) {
      filters.customer_ids.forEach((customer_ids) =>
        newFilters.push({ id: "customer_ids", value: customer_ids.value }),
      );
    }
    if (filters.archived) {
      newFilters.push({
        id: filters.archived.id,
        value: filters.archived.value,
      });
    }

    setSelectedFiltersData(newFilters);
  };

  useEffect(() => {
    projectStore.getTypeOfOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApply = async () => {
    setLoading(true);

    try {
      setFilters((prev) => ({
        ...prev,
        page: 1,
        limit: 30,
        search: "",
        sortBy: "created_date",
        sortType: SortType.DESC,
      }));

      await opsSyncAnalyticsDashboardStore.getData({
        pm_admin_id: selectedFilters.pm_admin_id,
        sales_admin_id: selectedFilters.sales_admin_id,
        archived: selectedFilters.archived,
        order_id: selectedFilters.order_id,
        order_type: selectedFilters.order_type,
        product_ids: selectedFilters.product_ids,
        customer_ids: selectedFilters.customer_ids,
        start_date: selectedFilters.start_date,
        end_date: selectedFilters.end_date,
      });

      opsSyncAnalyticsDashboardStore.clearLocalSearch();
    } catch (error) {
      showError("Error", "Failed to fetch the analytics dashboard details 2.");
    } finally {
      setTimeout(() => setLoading(false), 500);
    }
  };

  const handleRangeSelect = (range: {
    startDate: Dayjs | null;
    endDate: Dayjs | null;
  }) => {
    if (range.startDate && range.endDate) {
      setFilters((prev) => ({
        ...prev,
        start_date: range.startDate!,
        end_date: range.endDate!,
      }));
    }
  };

  const handleRemoveFilter = (index: number) => {
    const filterToRemove = selectedFiltersData[index];
    const updatedFilters = selectedFiltersData.filter((_, i) => i !== index);
    setSelectedFiltersData(updatedFilters);

    switch (filterToRemove.id) {
      case "pm_admin_id":
        setFilters((prev) => ({
          ...prev,
          pm_admin_id:
            filters.pm_admin_id?.filter(
              (item) => item.value !== filterToRemove.value,
            ) || [],
        }));
        break;

      case "sales_admin_id":
        setFilters((prev) => ({
          ...prev,
          sales_admin_id:
            filters.sales_admin_id?.filter(
              (item) => item.value !== filterToRemove.value,
            ) || [],
        }));
        break;

      case "order_type":
        setFilters((prev) => ({
          ...prev,
          order_type:
            prev.order_type?.filter(
              (item) => item.value !== filterToRemove.value,
            ) || [],
        }));
        break;
      case "order_id":
        setFilters((prev) => ({
          ...prev,
          order_id:
            prev.order_id?.filter(
              (item) => item.value !== filterToRemove.value,
            ) || [],
        }));
        break;
      case "product_ids":
        setFilters((prev) => ({
          ...prev,
          product_ids:
            prev.product_ids?.filter(
              (item) => item.value !== filterToRemove.value,
            ) || [],
        }));
        break;
      case "customer_ids":
        setFilters((prev) => ({
          ...prev,
          customer_ids:
            prev.customer_ids?.filter(
              (item) => item.value !== filterToRemove.value,
            ) || [],
        }));
        break;
      case "local_search":
        opsSyncAnalyticsDashboardStore.clearLocalSearch();
        break;
      default:
        break;
    }
  };

  const handleClearAllFilters = () => {
    setFilters((_) => ({
      ...initialFilters,
      pm_admin_id: [],
      sales_admin_id: [],
      order_id: [],
      order_type: [],
      product_ids: [],
      customer_ids: [],
      archived: archivedOptionsList.find((item) => item.value === "Active") || {
        id: "1",
        value: "Active",
      },
    }));

    setSelectedFiltersData([]);

    // Clear local search using store method
    opsSyncAnalyticsDashboardStore.clearLocalSearch();
  };

  const handleOrderIdSearch = (searchText?: string) => {
    if (apiTimer) clearTimeout(apiTimer);
    if (searchText && searchText.length > 0)
      apiTimer = setTimeout(() => {
        orderStore.searchOrderIds(searchText).catch((err) => {});
      }, 1500);
  };

  const handleAdminSearch = (searchText: string) => {
    if (searchText.length > 2) {
      TaskTrackerStore.getAdminBySearch(searchText).catch((err) => {
        showError("Error", err?.response?.data?.message);
      });
    }
  };

  const handleProductSearch = (searchText: string) => {
    if (apiTimer) clearTimeout(apiTimer);

    if (searchText && searchText.length > 2)
      apiTimer = setTimeout(() => {
        productStore
          .getProducts({
            limit: 30,
            page_no: 1,
            name: searchText,
            sortBy: "name",
            sortType: SortType.ASC,
          })
          .catch((err) => {
            console.log(err);
          });
      }, 1000);
  };

  const handleCustomerSearch = (searchText: string) => {
    if (apiTimer) clearTimeout(apiTimer);

    if (searchText && searchText.length > 2)
      apiTimer = setTimeout(() => {
        customerStore
          .getCustomers({
            limit: 30,
            page_no: 1,
            search_text: searchText,
            sort_by: "name",
            sort_type: SortType.ASC,
          })
          .catch((err) => {
            console.log(err);
          });
      }, 1000);
  };

  return (
    <>
      {userStore.hasPermission(EmployeeRoles.VIEW_OPS_DASHBOARD) ? (
        <>
          <LoaderOverlay loading={loading} />
          <Box
            sx={{
              margin: "8px",
              gap: 2,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                backgroundColor: theme.palette.customColors.white,
                padding: 4,
                borderRadius: 2,
                gap: 2,
              }}
            >
              <Grid2 container spacing={2} columns={12} sx={{ width: "100%" }}>
                <Grid2 size={{ lg: 3, md: 4, sm: 12, xs: 12 }}>
                  <CustomTypography
                    variant="heading8"
                    color={theme.palette.customColors.text}
                    style={{ marginBottom: 6 }}
                  >
                    Date Range
                  </CustomTypography>
                  <DateRangePickerNew
                    onRangeSelect={handleRangeSelect}
                    startDate={filters.start_date}
                    endDate={filters.end_date}
                  />
                </Grid2>
                <Grid2 size={{ lg: 3, md: 4, sm: 12, xs: 12 }}>
                  <CustomTypography
                    variant="heading8"
                    color={theme.palette.customColors.text}
                    style={{ marginBottom: 6 }}
                  >
                    {"Project POC"}
                  </CustomTypography>
                  <AppSearchDropDown2
                    showSelection={false}
                    isMultiSelect={true}
                    title="Select Poc"
                    fontSize="0.9rem"
                    value={filters.pm_admin_id}
                    list={
                      TaskTrackerStore?.admins?.map((admin: Admin) => ({
                        id: admin.id.toString(),
                        value: admin.name,
                      })) || []
                    }
                    local={false}
                    handleSearch={(pm_admin_id) =>
                      handleAdminSearch(pm_admin_id!)
                    }
                    handleSelect={(items) => {
                      setFilters((prev) => ({
                        ...prev,
                        pm_admin_id: items,
                      }));
                    }}
                  />
                </Grid2>
                <Grid2 size={{ lg: 3, md: 4, sm: 12, xs: 12 }}>
                  <CustomTypography
                    variant="heading8"
                    color={theme.palette.customColors.text}
                    style={{ marginBottom: 6 }}
                  >
                    {"Sales POC"}
                  </CustomTypography>
                  <AppSearchDropDown2
                    showSelection={false}
                    isMultiSelect={true}
                    title="Select Poc"
                    fontSize="0.9rem"
                    value={filters.sales_admin_id}
                    list={
                      TaskTrackerStore?.admins?.map((admin: Admin) => ({
                        id: admin.id.toString(),
                        value: admin.name,
                      })) || []
                    }
                    local={false}
                    handleSearch={(sales_admin_id) =>
                      handleAdminSearch(sales_admin_id!)
                    }
                    handleSelect={(items) => {
                      setFilters((prev) => ({
                        ...prev,
                        sales_admin_id: items,
                      }));
                    }}
                  />
                </Grid2>
                <Grid2 size={{ lg: 3, md: 4, sm: 12, xs: 12 }}>
                  <CustomTypography
                    variant="heading8"
                    color={theme.palette.customColors.text}
                    style={{ marginBottom: 6 }}
                  >
                    {"Project Status"}
                  </CustomTypography>
                  <AppDropDown
                    title=""
                    fontSize="0.7rem"
                    isMultiSelect={false}
                    value={filters.archived?.id || ""}
                    list={archivedOptionsList}
                    onSingleSelectionChange={(id) => {
                      const selected = archivedOptionsList.find(
                        (item) => item.id === id,
                      );
                      if (selected) {
                        setFilters((prev) => ({ ...prev, archived: selected }));
                      }
                    }}
                  />
                </Grid2>
              </Grid2>
              <Grid2 container spacing={2} columns={12} sx={{ width: "100%" }}>
                <Grid2 size={{ lg: 3, md: 4, sm: 12, xs: 12 }}>
                  <CustomTypography
                    variant="heading8"
                    color={theme.palette.customColors.text}
                    style={{ marginBottom: 6 }}
                  >
                    {"Order Type"}
                  </CustomTypography>
                  <AppDropDown
                    title=""
                    isMultiSelect={true}
                    valuesList={
                      filters.order_type?.map((item) => item.id) || []
                    }
                    list={
                      projectStore?.orderType?.map((order) => ({
                        id: order.id.toString(),
                        value: order.value,
                      })) || []
                    }
                    fontSize={"12px"}
                    onMultiSelectionChange={(ids) => {
                      setFilters((prev) => ({
                        ...prev,
                        order_type:
                          projectStore?.orderType?.filter((item) =>
                            ids.includes(item.id),
                          ) || [],
                      }));
                    }}
                  />
                </Grid2>
                <Grid2 size={{ lg: 3, md: 4, sm: 12, xs: 12 }}>
                  <CustomTypography
                    variant="heading8"
                    color={theme.palette.customColors.text}
                    style={{ marginBottom: 6 }}
                  >
                    Order ID
                  </CustomTypography>
                  <AppSearchDropDown2
                    showSelection={false}
                    isMultiSelect={true}
                    title="Order Id"
                    value={filters.order_id}
                    fontSize="12px"
                    list={orderStore.orderIds.map((item) => {
                      return { id: String(item), value: String(item) };
                    })}
                    local={false}
                    handleSearch={handleOrderIdSearch}
                    handleSelect={(items) => {
                      setFilters((prev) => ({
                        ...prev,
                        order_id: items,
                      }));
                    }}
                  />
                </Grid2>
                <Grid2 size={{ lg: 3, md: 4, sm: 12, xs: 12 }}>
                  <CustomTypography
                    variant="heading8"
                    color={theme.palette.customColors.text}
                    style={{ marginBottom: 6 }}
                  >
                    Product Name
                  </CustomTypography>
                  <AppSearchDropDown2
                    showSelection={false}
                    isMultiSelect={true}
                    title="Select Product"
                    fontSize="0.9rem"
                    value={filters.product_ids || []}
                    list={
                      productStore?.products?.map((product: Product) => ({
                        id: product.id?.toString() || "",
                        value: product.name || "",
                      })) || []
                    }
                    local={false}
                    handleSearch={(product) => handleProductSearch(product!)}
                    handleSelect={(items) => {
                      setFilters((prev) => ({
                        ...prev,
                        product_ids: items,
                      }));
                    }}
                  />
                </Grid2>
                <Grid2 size={{ lg: 3, md: 4, sm: 12, xs: 12 }}>
                  <CustomTypography
                    variant="heading8"
                    color={theme.palette.customColors.text}
                    style={{ marginBottom: 6 }}
                  >
                    Client Name
                  </CustomTypography>
                  <AppSearchDropDown2
                    showSelection={false}
                    isMultiSelect={true}
                    title="Select Client"
                    fontSize="0.9rem"
                    value={filters.customer_ids || []}
                    list={
                      customerStore?.customers?.map((customer: Customer) => ({
                        id: customer.id?.toString() || "",
                        value: customer.name || "",
                      })) || []
                    }
                    local={false}
                    handleSearch={(customer) => handleCustomerSearch(customer!)}
                    handleSelect={(items) => {
                      setFilters((prev) => ({
                        ...prev,
                        customer_ids: items,
                      }));
                    }}
                  />
                </Grid2>
              </Grid2>
              <Grid2
                container
                spacing={2}
                columns={12}
                sx={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Grid2 size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
                  <CustomTextField
                    width={"100%"}
                    placeholder="Search locally by order id, product id, product name or client name (Enter to search)"
                    value={opsSyncAnalyticsDashboardStore.localSearchTerm}
                    onChange={(e) => {
                      opsSyncAnalyticsDashboardStore.setLocalSearchTerm(
                        e.target.value,
                      );
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        opsSyncAnalyticsDashboardStore.applyLocalSearch();
                      }
                    }}
                  />
                </Grid2>
                <Grid2
                  size={{ lg: 4, md: 4, sm: 12, xs: 12 }}
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "flex-end",
                    gap: 2,
                  }}
                >
                  <AppButton
                    variant="primaryBlock"
                    label="Search"
                    onClick={handleApply}
                    customStyles={{ width: "100%" }}
                    disabled={loading}
                  />
                  <AppButton
                    variant="transparentPrimary"
                    label="Clear Filters"
                    customStyles={{ width: "100%" }}
                    onClick={() => handleClearAllFilters()}
                  />
                </Grid2>
              </Grid2>

              <Grid2 container spacing={2} columns={12} sx={{ width: "100%" }}>
                {selectedFiltersData.length > 0 && (
                  <Box sx={{ paddingTop: "15px" }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 2,
                        borderRadius: 2,
                      }}
                    >
                      {selectedFiltersData.map((filter, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            backgroundColor:
                              theme.palette.customColors.lightPurple,
                            borderRadius: "8px",
                            border: "1px solid",
                            borderColor: theme.palette.customColors.blackBorder,
                            height: "34px",
                            paddingX: "6px",
                          }}
                        >
                          <CustomTypography
                            variant="heading9"
                            color={theme.palette.customColors.text}
                            style={{ marginRight: "5px" }}
                          >
                            {filter.value}
                          </CustomTypography>
                          <Button
                            onClick={() => handleRemoveFilter(index)}
                            sx={{
                              width: "14px",
                              height: "14px",
                              minWidth: "14px",
                              minHeight: "14px",
                              fontSize: "15px",
                              padding: "0px",
                              border: "1px solid #E2E2E2",
                              borderRadius: "50%",
                              backgroundColor: theme.palette.customColors.white,
                              cursor: "pointer",
                              overflow: "hidden",
                              "&:hover": {
                                backgroundColor:
                                  theme.palette.customColors.lightGreyColor,
                              },
                            }}
                          >
                            ×
                          </Button>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Grid2>
            </Box>

            <Box sx={{ marginBottom: 2 }}>
              <CustomTypography
                variant="heading8"
                color={theme.palette.customColors.blackBorder}
              >
                <span style={{ fontStyle: "italic" }}>Note:</span> The analytics
                data is cached. Recent changes may take up to 30 minutes to
                reflect.
              </CustomTypography>
            </Box>

            <OpsSyncAnalyticsTable
              data={opsSyncAnalyticsDashboardStore.filteredData}
            />
          </Box>
        </>
      ) : (
        <PermissionBlockLabel />
      )}
    </>
  );
};

export default observer(OpsSyncAnalyticsDashboard);
